import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { ChevronDown, X, Check, Search, FlaskConical, Info } from 'lucide-react';
import FactorCard from '../FactorCard';
import { createFactorSearchIndex, searchFactors, sortFactorsByRelevance } from '../../utils/factorSearch';
import { factorServable } from '../../utils/factorServable';
import axios from 'axios';
import { API_URL } from '../../utils/apiConfig';

/**
 * FactorPickerInline — Replaces raw <select> for factor selection in product wizard.
 * Shows an inline expandable panel with subcategory filter + search + FactorCard grid.
 *
 * Props:
 *   factors       — full list of factors for the category
 *   categoryCode  — category code (e.g. "electricite") for subcategory loading
 *   selectedFactorId — currently selected factor ID
 *   onSelectFactor   — callback(factor) when user picks a factor
 *   label         — label shown above the picker (e.g. "Facteur d'émission")
 */
const FactorPickerInline = ({ factors, categoryCode, selectedFactorId, onSelectFactor, label, autoOpen = false }) => {
  const { isDark } = useTheme();
  const { language, t } = useLanguage();
  const [open, setOpen] = useState(autoOpen);
  const [subcategories, setSubcategories] = useState([]);
  const [activeSubcat, setActiveSubcat] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(null);
  const [showExpert, setShowExpert] = useState(false);

  // Load subcategories once
  useEffect(() => {
    if (!categoryCode) return;
    axios.get(`${API_URL}/api/subcategories?category=${categoryCode}`).then(res => {
      setSubcategories(res.data || []);
    }).catch(() => {});
  }, [categoryCode]);

  // Derive unique subcategory codes present in factors — primaire ET
  // alternative_subcategories (fix oubli W6-P5b : l'onglet d'une subcat alt
  // doit exister même si aucun facteur ne l'a en primaire).
  const availableSubcats = useMemo(() => {
    if (!factors?.length) return [];
    const codes = new Set();
    factors.forEach(f => {
      if (f.subcategory) codes.add(f.subcategory);
      (f.alternative_subcategories || []).forEach(c => codes.add(c));
    });
    return subcategories.filter(s => codes.has(s.code));
  }, [factors, subcategories]);

  // Filter factors by subcategory (primaire OU alternative) + public/expert
  const filteredFactors = useMemo(() => {
    if (!factors) return [];
    let list = factors;
    if (activeSubcat) {
      list = list.filter(f => f.subcategory === activeSubcat
        || f.alternative_subcategories?.includes(activeSubcat));
    }
    list = list.filter(f => factorServable(f, showExpert));
    return list;
  }, [factors, activeSubcat, showExpert]);

  // Build search index
  useEffect(() => {
    if (filteredFactors.length > 0) {
      setSearchIndex(createFactorSearchIndex(filteredFactors));
    }
  }, [filteredFactors]);

  // Display factors (searched or sorted)
  const displayFactors = useMemo(() => {
    if (!filteredFactors.length) return [];
    if (searchQuery.trim().length >= 2 && searchIndex) {
      return searchFactors(searchIndex, searchQuery) || [];
    }
    return sortFactorsByRelevance(filteredFactors, language);
  }, [filteredFactors, searchQuery, searchIndex, language]);

  const expertCount = useMemo(() => {
    let list = factors || [];
    if (activeSubcat) list = list.filter(f => f.subcategory === activeSubcat
      || f.alternative_subcategories?.includes(activeSubcat));
    return list.filter(f => f.is_public === false).length;
  }, [factors, activeSubcat]);

  const selectedFactor = useMemo(() => {
    if (!selectedFactorId || !factors) return null;
    return factors.find(f => f.id === selectedFactorId);
  }, [selectedFactorId, factors]);

  const handleSelect = useCallback((factor) => {
    onSelectFactor(factor);
    setOpen(false);
    setSearchQuery('');
  }, [onSelectFactor]);

  const subcatLabel = (code) => {
    const sc = subcategories.find(s => s.code === code);
    if (!sc) return code;
    return language === 'de' ? (sc.name_de || sc.name_fr) : sc.name_fr;
  };

  const factorDisplayName = (f) => {
    if (!f) return '';
    return language === 'de'
      ? (f.name_simple_de || f.name_simple_fr || f.source_product_name)
      : (f.name_simple_fr || f.source_product_name);
  };

  const impact = selectedFactor?.impacts?.[0];

  // --- Collapsed state: show selected factor or "choose" button ---
  if (!open) {
    return (
      <div>
        {label && <label className={`block text-xs mb-1.5 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{label}</label>}
        {selectedFactor ? (
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors ${
            isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-white border-gray-200'
          }`}>
            <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className={`text-sm font-medium truncate block ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {factorDisplayName(selectedFactor)}
              </span>
              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                {impact ? `${impact.value < 0.001 ? impact.value.toExponential(2) : impact.value.toFixed(3)} ${impact.unit}` : ''}
                {selectedFactor.source ? ` — ${selectedFactor.source}` : ''}
              </span>
            </div>
            <button onClick={() => setOpen(true)}
              className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                isDark ? 'text-blue-400 hover:bg-blue-500/10' : 'text-blue-600 hover:bg-blue-50'
              }`} data-testid="factor-picker-change">
              {t('factorPicker.change')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setOpen(true)}
            data-testid="factor-picker-open"
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border transition-colors text-sm ${
              isDark
                ? 'bg-slate-700/50 border-slate-600 text-slate-400 hover:border-blue-500/50'
                : 'bg-white border-gray-200 text-gray-400 hover:border-blue-400'
            }`}
          >
            <span>{t('factorPicker.choose')}</span>
            <ChevronDown className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // --- Expanded state: subcategory filter + search + cards ---
  return (
    <div>
      {label && <label className={`block text-xs mb-1.5 font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{label}</label>}
      <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-slate-800/80 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-3 py-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {displayFactors.length} {t(displayFactors.length === 1 ? 'factorPicker.factorSingular' : 'factorPicker.factorPlural')}
          </span>
          <div className="flex items-center gap-2">
            {expertCount > 0 && (
              <button onClick={() => setShowExpert(!showExpert)}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${
                  showExpert
                    ? isDark ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-amber-50 border-amber-300 text-amber-700'
                    : isDark ? 'bg-slate-700 border-slate-600 text-slate-400' : 'bg-white border-gray-200 text-gray-500'
                }`} data-testid="factor-picker-expert">
                <FlaskConical className="w-3 h-3" />
                {showExpert ? t('factorPicker.experts') : `+${expertCount}`}
              </button>
            )}
            <button onClick={() => { setOpen(false); setSearchQuery(''); }}
              className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-200 text-gray-400'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className={`px-3 pt-2 ${availableSubcats.length > 0 ? '' : 'pb-2'}`}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('factorPicker.searchPlaceholder')}
              data-testid="factor-picker-search"
              autoFocus
              className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm transition-colors focus:ring-1 focus:ring-blue-500 ${
                isDark ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : 'bg-white border-gray-200 placeholder:text-gray-400'
              }`} />
          </div>
        </div>

        {/* Subcategory chips */}
        {availableSubcats.length > 1 && (
          <div className="flex gap-1.5 flex-wrap px-3 py-2">
            <button onClick={() => setActiveSubcat(null)}
              className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                !activeSubcat
                  ? isDark ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40' : 'bg-blue-50 text-blue-600 border border-blue-200'
                  : isDark ? 'text-slate-400 hover:text-slate-200 border border-transparent' : 'text-gray-500 hover:text-gray-700 border border-transparent'
              }`} data-testid="factor-picker-subcat-all">
            {t('factorPicker.all')}
            </button>
            {availableSubcats.map(sc => (
              <button key={sc.code} onClick={() => setActiveSubcat(sc.code)}
                className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                  activeSubcat === sc.code
                    ? isDark ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40' : 'bg-blue-50 text-blue-600 border border-blue-200'
                    : isDark ? 'text-slate-400 hover:text-slate-200 border border-transparent' : 'text-gray-500 hover:text-gray-700 border border-transparent'
                }`} data-testid={`factor-picker-subcat-${sc.code}`}>
                {subcatLabel(sc.code)}
              </button>
            ))}
          </div>
        )}

        {/* Factor grid */}
        <div className="px-3 pb-3 max-h-60 overflow-y-auto">
          {displayFactors.length === 0 ? (
            <div className={`text-center py-6 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              <Info className="w-6 h-6 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('factorPicker.noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {displayFactors.slice(0, 50).map(f => (
                <FactorCard key={f.id} factor={f} language={language}
                  isSelected={f.id === selectedFactorId}
                  onClick={() => handleSelect(f)} />
              ))}
            </div>
          )}
          {displayFactors.length > 50 && (
            <p className={`text-xs text-center mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              {displayFactors.length - 50} {t('factorPicker.overflowHint')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactorPickerInline;
